/**
 * Case search drawer (FR-04, sc-11033): results of the top bar's "Search case",
 * beside the viewer. Hits are grouped per document with context; clicking one
 * shows it in the viewer, the checked ones (or all) are redacted as approved
 * suggestions with a reason and a category.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { apiHelpers, casesApi } from '../../lib/api';
import { getEntityTypeConfig, pickerEntityTypes, useDetectionCategories } from '../../lib/categories';
import type {
  CaseSearchDocument,
  CaseSearchHit,
  CaseSearchMode,
  CaseSearchRedactResult,
  CaseSearchResult,
  RedactionReason,
} from '../../types';
import { SearchableReasonSelect } from './SearchableReasonSelect';
import { cn } from '../ui/utils';
import {
  canSearch,
  countLabel,
  documentStatusLabel,
  documentToggle,
  outOfDateIds,
  plural,
  pruneSelection,
  resultMessage,
  rulePreview,
  selectableHits,
  toggleDocument,
  toggleHit,
} from './caseSearch';

interface CaseSearchDrawerProps {
  caseId: string;
  /** The submitted query (the top bar field). */
  query: string;
  onClose: () => void;
  /** Open the hit's document on its page with the hit highlighted. */
  onShowHit: (hit: CaseSearchHit, doc: CaseSearchDocument) => void;
  currentHitId: string | null;
  reasons: RedactionReason[];
  /** After a redaction: reload the case documents and the open document. */
  onRedacted: (outcome: CaseSearchRedactResult) => void;
}

export function CaseSearchDrawer({ caseId, query, onClose, onShowHit, currentHitId, reasons, onRedacted }: CaseSearchDrawerProps) {
  useDetectionCategories(); // category labels; re-render once they load
  const [mode, setMode] = useState<CaseSearchMode>('loose');
  const [result, setResult] = useState<CaseSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('CUSTOM');
  const [addRule, setAddRule] = useState(false);
  const [redacting, setRedacting] = useState(false);
  const [outcome, setOutcome] = useState<CaseSearchRedactResult | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [refinalize, setRefinalize] = useState<'idle' | 'running' | 'queued' | 'failed'>('idle');

  useEffect(() => {
    if (!canSearch(query)) {
      setResult(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    casesApi
      .searchCase(caseId, query.trim(), mode, controller.signal)
      .then((res) => {
        setResult(res);
        setSelected((prev) => pruneSelection(prev, res));
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(apiHelpers.handleError(err));
        setResult(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [caseId, query, mode, reloadTick]);

  // A new query starts a fresh result message.
  useEffect(() => {
    setOutcome(null);
    setMessage(null);
    setRefinalize('idle');
  }, [query, mode]);

  const open = useMemo(() => selectableHits(result), [result]);
  const picked = open.filter((h) => selected.has(h.id));
  const categoryOptions = pickerEntityTypes();
  const categoryLabel = getEntityTypeConfig(category).label;
  const staleIds = outOfDateIds(outcome);

  const redact = async (hitIds: string[] | undefined) => {
    if (!result || redacting) return;
    setRedacting(true);
    setMessage(null);
    try {
      const res = await casesApi.redactSearchHits(caseId, {
        query: result.query,
        mode,
        hit_ids: hitIds,
        entity_type: category,
        reason: reason || undefined,
        add_case_rule: addRule,
      });
      setOutcome(res);
      setMessage(resultMessage(res, categoryLabel));
      setRefinalize('idle');
      setSelected(new Set());
      setReloadTick((t) => t + 1);
      onRedacted(res);
    } catch (err) {
      setMessage(apiHelpers.handleError(err));
    } finally {
      setRedacting(false);
    }
  };

  const runRefinalize = async () => {
    if (staleIds.length === 0) return;
    setRefinalize('running');
    try {
      const res = await casesApi.finalizeBatch(caseId, staleIds, 'refinalize');
      setRefinalize(res.queued.length > 0 ? 'queued' : 'failed');
      onRedacted(outcome!);
    } catch {
      setRefinalize('failed');
    }
  };

  return (
    <aside
      className="w-[380px] shrink-0 border-l border-border bg-white flex flex-col min-h-0"
      aria-label="Case search results"
      data-testid="case-search-drawer"
    >
      <div className="px-3 pt-3 pb-2 border-b border-gray-100 shrink-0">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">Case search</span>
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded hover:bg-gray-100 text-gray-600"
            aria-label="Close search"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 text-xs text-gray-600">
          <span data-testid="case-search-count">
            {loading ? (
              <span className="inline-flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Searching…
              </span>
            ) : (
              <strong className="text-gray-900">{countLabel(result)}</strong>
            )}
          </span>
          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={mode === 'loose'}
              onChange={(e) => setMode(e.target.checked ? 'loose' : 'exact')}
              aria-label="Ignore spaces and dashes"
            />
            Ignore spaces and dashes
          </label>
        </div>
        {result && result.skipped_documents.length > 0 && (
          <p className="mt-1 text-[11px] text-amber-700">
            {plural(result.skipped_documents.length, 'document')} not searched yet (still processing).
          </p>
        )}
        {result?.truncated && (
          <p className="mt-1 text-[11px] text-amber-700">Too many hits: showing the first ones. Narrow the search.</p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {message && (
          <div className="m-3 rounded-md border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-700" data-testid="case-search-message">
            <p>{message}</p>
            {staleIds.length > 0 && refinalize !== 'queued' && (
              <div className="mt-2 rounded border border-rose-200 bg-rose-50 p-2" data-testid="case-search-stale">
                <p className="text-rose-800">
                  {outcome!.out_of_date_documents.map((d) => d.name).join(', ')}{' '}
                  {staleIds.length === 1 ? 'was' : 'were'} finalized before: now Out of date.
                </p>
                <button
                  type="button"
                  className="mt-1.5 inline-flex items-center gap-1 rounded bg-gray-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-gray-700 disabled:opacity-60"
                  onClick={runRefinalize}
                  disabled={refinalize === 'running'}
                >
                  {refinalize === 'running' && <Loader2 className="h-3 w-3 animate-spin" />}
                  Re-finalize {staleIds.length}
                </button>
                {refinalize === 'failed' && (
                  <p className="mt-1 text-rose-800">Could not queue it: use Finish in the review panel.</p>
                )}
              </div>
            )}
            {refinalize === 'queued' && (
              <p className="mt-1 text-green-700">Re-finalize queued for {plural(staleIds.length, 'document')}.</p>
            )}
          </div>
        )}
        {!canSearch(query) && (
          <p className="p-4 text-sm text-gray-500">Type at least 2 letters or digits in “Search case”.</p>
        )}
        {error && <p className="p-4 text-sm text-red-600">{error}</p>}
        {result && !loading && result.total_hits === 0 && (
          <p className="p-4 text-sm text-gray-500" data-testid="case-search-empty">
            No hits in {plural(result.searched_documents, 'document')}.
            {mode === 'exact' ? ' Tick “Ignore spaces and dashes” to find other spellings.' : ''}
          </p>
        )}
        {result?.documents.map((doc) => {
          const toggle = documentToggle(doc, selected);
          return (
            <div key={doc.document_id} data-testid="case-search-group">
              <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-1.5 bg-gray-50 border-y border-gray-100">
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold text-gray-900" title={doc.name}>{doc.name}</div>
                  <div className="text-[11px] text-gray-500">
                    {plural(doc.hits.length, 'hit')} ·{' '}
                    <span className={cn(doc.out_of_date && 'font-medium text-rose-700')} data-testid="case-search-doc-status">
                      {documentStatusLabel(doc)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={toggle.disabled}
                  onClick={() => setSelected((prev) => toggleDocument(prev, doc))}
                  className="shrink-0 rounded border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-50 disabled:cursor-default disabled:opacity-60"
                >
                  {toggle.label}
                </button>
              </div>
              {doc.hits.map((hit) => (
                <div
                  key={hit.id}
                  className={cn(
                    'flex items-start gap-2 border-b border-gray-100 px-3 py-1.5 text-xs text-gray-700',
                    currentHitId === hit.id ? 'bg-orange-50' : 'hover:bg-gray-50',
                  )}
                  data-testid="case-search-hit"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={hit.redacted || selected.has(hit.id)}
                    disabled={hit.redacted || !hit.redactable}
                    onChange={() => setSelected((prev) => toggleHit(prev, hit.id))}
                    aria-label={`Select hit ${hit.page != null ? `on page ${hit.page + 1} ` : ''}of ${doc.name}`}
                  />
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-start gap-2 text-left"
                    onClick={() => onShowHit(hit, doc)}
                    title={hit.redactable ? 'Show in the document' : 'No position on the page: it cannot be redacted from here'}
                  >
                    <span className="w-9 shrink-0 text-[11px] text-gray-400">
                      {hit.page != null ? `p. ${hit.page + 1}` : 'text'}
                    </span>
                    <span className="min-w-0 flex-1 break-words">
                      …{hit.before}
                      <mark
                        className={cn('rounded-sm px-px', hit.redacted ? 'bg-gray-900 text-white' : 'bg-yellow-200 text-gray-900')}
                      >
                        {hit.text}
                      </mark>
                      {hit.after}…
                    </span>
                    {hit.variant && !hit.redacted && (
                      <span className="shrink-0 rounded-sm bg-amber-100 px-1 py-px text-[10px] font-semibold uppercase text-amber-800">
                        spacing
                      </span>
                    )}
                    {hit.redacted && <Check className="h-3.5 w-3.5 shrink-0 text-green-700" aria-label="Redacted" />}
                  </button>
                </div>
              ))}
            </div>
          );
        })}

      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <SearchableReasonSelect reasons={reasons} value={reason} onChange={setReason} placeholder="Reason…" />
          <select
            aria-label="Category"
            className="h-9 rounded-md border border-gray-200 bg-white px-2 text-xs text-gray-900"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {(categoryOptions.includes(category) ? categoryOptions : [category, ...categoryOptions]).map((key) => (
              <option key={key} value={key}>
                {getEntityTypeConfig(key).label}
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-start gap-1.5 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={addRule} onChange={(e) => setAddRule(e.target.checked)} />
          <span>
            Also always redact in this case
            {addRule && result && (
              <span className="mt-0.5 block text-[11px] text-gray-500" data-testid="case-search-rule-preview">
                {rulePreview(result.query, result)}
              </span>
            )}
          </span>
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            className="flex-1 rounded bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-900 hover:bg-gray-200 disabled:cursor-default disabled:text-gray-400"
            disabled={picked.length === 0 || redacting}
            onClick={() => redact(picked.map((h) => h.id))}
          >
            Redact selected ({picked.length})
          </button>
          <button
            type="button"
            className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-gray-900 px-2 py-1.5 text-xs font-medium text-white hover:bg-gray-700 disabled:cursor-default disabled:bg-green-100 disabled:text-green-800"
            disabled={open.length === 0 || redacting}
            onClick={() => redact(undefined)}
          >
            {redacting && <Loader2 className="h-3 w-3 animate-spin" />}
            {open.length > 0 || !result || result.total_hits === 0 ? `Redact all (${open.length})` : 'All redacted'}
          </button>
        </div>
      </div>
    </aside>
  );
}
